import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { User, Session } from '@supabase/supabase-js';

type QuestionType = {
  id: number;
  question: string;
  description?: string;
  isUsername?: boolean;
  options?: Array<{
    value: string;
    label: string;
    description: string;
  }>;
};

const usernameQuestion: QuestionType = {
  id: 0,
  question: "Choose your display name",
  description: "This is how you'll appear in the community. You can always post anonymously too.",
  isUsername: true
};

const questions: QuestionType[] = [
  {
    id: 1,
    question: "Where are you in your journey?",
    options: [
      { value: "perimenopause", label: "I think I'm in perimenopause", description: "Experiencing irregular cycles or new symptoms" },
      { value: "menopause", label: "I'm in menopause", description: "No period for 12+ months" },
      { value: "postmenopause", label: "I'm post-menopause", description: "Several years past my last period" },
      { value: "unsure", label: "I'm not sure", description: "Help me figure it out" }
    ]
  },
  {
    id: 2,
    question: "Which symptoms are most challenging for you? (Choose your top concern)",
    options: [
      { value: "metabolic", label: "The Metabolic Shift", description: "Weight changes, hot flashes, energy dips" },
      { value: "neuro", label: "The Neuro-Wired", description: "Brain fog, mood swings, anxiety, sleep issues" },
      { value: "urogenital", label: "The Urogenital Focus", description: "Vaginal dryness, bladder changes, libido shifts" },
      { value: "physical", label: "Physical Changes", description: "Joint pain, muscle loss, skin changes" }
    ]
  },
  {
    id: 3,
    question: "What's your primary goal right now?",
    options: [
      { value: "understand", label: "Understanding what's happening", description: "I need information and clarity" },
      { value: "manage", label: "Managing my symptoms", description: "I want practical relief strategies" },
      { value: "connect", label: "Connecting with others", description: "I need community and support" },
      { value: "optimize", label: "Optimizing my health", description: "I want to thrive through this transition" }
    ]
  },
  {
    id: 4,
    question: "How would you rate your current knowledge about perimenopause/menopause?",
    options: [
      { value: "beginner", label: "Just Starting", description: "This is all new to me" },
      { value: "learning", label: "Learning", description: "I know some basics" },
      { value: "informed", label: "Well-Informed", description: "I've done my research" },
      { value: "expert", label: "Very Knowledgeable", description: "I understand this deeply" }
    ]
  }
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [displayName, setDisplayName] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    /**
     * USER JOURNEY: Onboarding Flow (Legacy - kept for existing users)
     * 
     * Note: New users now go through /profile-setup instead.
     * This onboarding page is maintained for backwards compatibility.
     * 
     * Flow: /onboarding → / (home page)
     * 
     * User must be authenticated to access this page.
     */
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (!session?.user) {
          setTimeout(() => {
            navigate("/auth");
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  

  const handleAnswer = (value: string) => {
    setAnswers({ ...answers, [currentQuestion]: value });
  };

  const validateUsername = async (username: string): Promise<boolean> => {
    if (!username || username.trim().length < 3) {
      setUsernameError("Display name must be at least 3 characters");
      return false;
    }

    if (username.trim().length > 20) {
      setUsernameError("Display name must be less than 20 characters");
      return false;
    }

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setUsernameError("Only letters, numbers, and underscores allowed");
      return false;
    }

    // Check if username is already taken
    const { data, error } = await supabase
      .from('profiles')
      .select('display_name')
      .eq('display_name', username.trim())
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      setUsernameError("Error checking username availability");
      return false;
    }

    if (data) {
      setUsernameError("This display name is already taken");
      return false;
    }

    setUsernameError("");
    return true;
  };

  const handleNext = async () => {
    if (currentQuestion === 0) {
      // Username question
      const isValid = await validateUsername(displayName);
      if (!isValid) return;
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentQuestion < questions.length) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      await saveProfile();
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Convert answers to proper format - DON'T mark onboarding as completed yet
      const profileData = {
        user_id: user.id,
        display_name: displayName.trim(),
        journey_stage: answers[1],
        symptoms: answers[2] ? [answers[2]] : [],
        goals: answers[3] ? [answers[3]] : [],
        knowledge_level: answers[4],
        onboarding_completed: false, // Will be set to true after welcome screen
      };

      const { error } = await supabase
        .from("profiles")
        .upsert(profileData, { onConflict: "user_id" });

      if (error) throw error;

      toast({
        title: "Profile saved!",
        description: "Your preferences have been saved successfully.",
      });

      // Onboarding complete - redirect to home page
      navigate("/");
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const currentQ = currentQuestion === 0 ? usernameQuestion : questions[currentQuestion - 1];
  const hasAnswer = currentQuestion === 0 ? displayName.trim().length >= 3 : answers[currentQuestion] !== undefined;
  const progress = ((currentQuestion) / (questions.length + 1)) * 100;

  return (
    <div className="min-h-screen bg-gradient-calm flex items-center justify-center p-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center gap-2 mb-4 text-primary">
            <Sparkles className="h-6 w-6" />
            <span className="text-lg font-medium">Her Season</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Let's Create Your Profile
          </h1>
          <p className="text-lg text-muted-foreground">
            Help us personalize your experience
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8 animate-fade-in">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {currentQuestion + 1} of {questions.length + 1}</span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="p-8 shadow-lifted animate-scale-in">
          <h2 className="text-2xl font-semibold mb-4 text-foreground">
            {currentQ.question}
          </h2>
          {currentQ.description && (
            <p className="text-muted-foreground mb-6">{currentQ.description}</p>
          )}

          {currentQuestion === 0 ? (
            // Username input
            <div className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="e.g., SunflowerMom, WiseWoman, etc."
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setUsernameError("");
                  }}
                  maxLength={20}
                  className="text-lg"
                />
                {usernameError && (
                  <p className="text-sm text-destructive mt-2">{usernameError}</p>
                )}
                <p className="text-sm text-muted-foreground mt-2">
                  3-20 characters. Letters, numbers, and underscores only.
                </p>
              </div>
            </div>
          ) : (
            // Regular question options
            <RadioGroup
              value={answers[currentQuestion]}
              onValueChange={handleAnswer}
              className="space-y-4"
            >
              {currentQ.options?.map((option) => (
                <div
                  key={option.value}
                  className={`relative flex items-start space-x-3 rounded-xl border-2 p-5 cursor-pointer transition-all hover:shadow-soft ${
                    answers[currentQuestion] === option.value
                      ? "border-primary bg-primary-soft"
                      : "border-border hover:border-primary/30"
                  }`}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                  <Label
                    htmlFor={option.value}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-semibold text-foreground mb-1">
                      {option.label}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {option.description}
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentQuestion === 0}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={!hasAnswer || loading}
              className="gap-2"
            >
              {loading ? "Saving..." : currentQuestion === questions.length - 1 ? "Complete" : "Next"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* Skip Option */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate("/tracker")}
            className="text-muted-foreground hover:text-foreground transition-colors underline"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
